/**
 * The chat log and composer, shared by the party room and direct messages.
 *
 * Messages group by author the way Discord does: a run from the same person
 * within a few minutes shows one header, and the rest carry only a hover
 * timestamp.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { copyText } from '@/lib/clipboard';
import { burstDelay } from '@/lib/burst';
import { shrinkImage } from '@/lib/shrink';
import { Lightbox } from './Lightbox';
import { Sheet } from './Sheet';
import { Forward } from './Forward';
import { EmojiImg } from './EmojiImg';
import { EmojiText } from './EmojiText';
import { MentionBox, mentionAt, type MentionState } from './MentionBox';
import { api, serverUrl } from '@/lib/api';
import type { Attachment, Message, PublicUser } from '@/lib/types';

/** A file on its way up, or one that failed and can be tried again. */
type UploadRow = { key: string; name: string; size: number; percent: number; file: File; failed?: string };

/** Bytes as a human number, matching what the server says in its errors. */
function mb(bytes: number): string {
  const n = bytes / (1024 * 1024);
  return (n >= 100 ? Math.round(n) : Math.round(n * 10) / 10) + ' MB';
}
import { cn } from '@/lib/cn';
import { inTextField } from '@/lib/keybinds';
import { toggleReaction } from '@/lib/optimistic';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AudioAttachment, SafeImage, VideoAttachment, VoiceNote, isVoiceNote } from './ChatMedia';
import { VoiceRecorder } from './VoiceRecorder';
import { gameBanner } from '@/lib/gameArt';
import type { Party } from '@/lib/types';
import { useSession } from '@/stores/session';
import { openExternal } from '@/lib/bridge';
import { go } from '@/lib/hash';
import { Avatar } from './Avatar';
import { CrewTag } from './Crew';
import { RoleTag, UserName, openUserCard } from './user';
import { Button, IconButton, Modal, Textarea, useConfirm, useToast } from './ui';
import { EmojiPicker } from './EmojiPicker';
import { NewPoll, PollCard } from './Poll';
import { ago } from '@/lib/notifications';
import { ClipMarks, Transcript, Translated } from './Assist';
import { openThread } from '@/lib/thread';
import { useContextMenu, type MenuItem } from './Menu';
import { Icon } from '@/icons/Icon';

const GROUP_WINDOW = 5 * 60 * 1000;

/**
 * Where each remembered conversation was scrolled to, as distance from the
 * bottom (which survives new messages above and below) and whether it was
 * following the newest message.
 */
const scrollMemory = new Map<string, { fromBottom: number; stick: boolean }>();

export function ChatLog({
  messages, canModerate, emptyHint, onReply, onPin, onLoadOlder, hasOlder, loadingOlder, onBottomChange, memoryKey, paused
}: {
  /** A conversation between two people. Kept for callers; names now show here too, as everywhere else. */
  pair?: boolean;
  messages: Message[];
  canModerate?: boolean;
  emptyHint?: string;
  onReply?: (m: Message) => void;
  /** Passed when you may pin in this conversation. */
  onPin?: (m: Message) => void;
  /** Fetches the page before the oldest message on screen. */
  onLoadOlder?: () => void;
  hasOlder?: boolean;
  loadingOlder?: boolean;
  /**
   * Told whenever the newest message comes into or goes out of view. This is
   * what decides whether a conversation has been read - never merely that it
   * was opened.
   */
  onBottomChange?: (atBottom: boolean) => void;
  /** Remember the scroll position under this key, and come back to it. */
  memoryKey?: string;
  /** Hidden for now (minimized, another tab): keep the place, record nothing. */
  paused?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  /* Where the scroll was before older messages went in above it. */
  const anchor = useRef<{ height: number; top: number } | null>(null);
  const settings = useSession((s) => s.settings);
  /* Messages that arrived below while you were reading further up. */
  const [newBelow, setNewBelow] = useState(0);
  const lastCount = useRef(messages.length);
  const lastId = useRef(messages[messages.length - 1]?.id);
  /* False until this log has been placed once. The first placement is a
     jump - there is nowhere to glide from - and every one after it glides. */
  const glided = useRef(false);

  useEffect(() => {
    const newest = messages[messages.length - 1]?.id;
    const grew = messages.length > lastCount.current && newest !== lastId.current;
    if (grew && !stick.current) setNewBelow((n) => n + (messages.length - lastCount.current));
    lastCount.current = messages.length;
    lastId.current = newest;
  }, [messages]);

  useEffect(() => { onBottomChange?.(stick.current); }, []);

  function jumpToNewest() {
    const node = ref.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    setNewBelow(0);
  }

  // Follow the bottom only when you were already there - yanking the view
  // down while somebody is reading history is worse than a missed message.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    /*
     * Older messages went in above: hold your place.
     *
     * Adding a page at the top pushes everything down by however tall it is,
     * so without this the view jumps and you lose the line you were reading -
     * which then triggers another load, and another.
     */
    const held = anchor.current;
    if (held) {
      anchor.current = null;
      node.scrollTop = held.top + (node.scrollHeight - held.height);
      return;
    }

    if (!stick.current) return;

    /*
     * Following the bottom used to be one assignment, so a message arriving
     * teleported the log and you lost your place in the sentence you were
     * halfway through reading. Gliding the last hundred pixels is what makes
     * a new line read as having arrived under the one before it.
     *
     * Three things still jump. The first paint of a log, because there is no
     * "before" to travel from; a gap wide enough that the glide would be a
     * blur rather than a movement - a picture finishing its load, a page of
     * history landing; and anybody who asked for less motion.
     */
    const gap = node.scrollHeight - node.scrollTop - node.clientHeight;
    const jump = !glided.current || gap > 1400
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    glided.current = true;
    if (jump) { node.scrollTop = node.scrollHeight; return; }
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  /*
   * Back where you were.
   *
   * On opening (once there are messages to scroll) and whenever the log comes
   * back from being hidden, the remembered place wins: the newest message if
   * you were following it, otherwise the same line you were reading - even if
   * messages arrived meanwhile.
   */
  const hasMessages = messages.length > 0;
  useLayoutEffect(() => {
    if (paused || !memoryKey || !hasMessages) return;
    const node = ref.current;
    const saved = scrollMemory.get(memoryKey);
    if (!node || !saved) return;
    if (saved.stick) {
      stick.current = true;
      /* Arriving at a conversation puts you at the bottom of it at once.
         Gliding here would show you the middle of a log you never asked
         to see. */
      glided.current = false;
      node.scrollTop = node.scrollHeight;
    } else {
      stick.current = false;
      node.scrollTop = Math.max(0, node.scrollHeight - saved.fromBottom);
    }
    onBottomChange?.(stick.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, memoryKey, hasMessages]);

  function onScroll() {
    const node = ref.current;
    if (!node) return;
    const was = stick.current;
    stick.current = node.scrollHeight - node.scrollTop - node.clientHeight < 90;
    if (stick.current !== was) onBottomChange?.(stick.current);
    if (stick.current) setNewBelow(0);
    if (memoryKey && !paused) scrollMemory.set(memoryKey, { fromBottom: node.scrollHeight - node.scrollTop, stick: stick.current });

    // Near the top and there is more behind: fetch the previous page and
      // remember where we were so it can be restored once it lands.
    if (node.scrollTop < 240 && hasOlder && !loadingOlder && onLoadOlder) {
      anchor.current = { height: node.scrollHeight, top: node.scrollTop };
      onLoadOlder();
    }
  }

  if (!messages.length) {
    return (
      <div ref={ref} className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid h-full place-items-center text-[13px] text-mute">
          {emptyHint ?? 'Say something.'}
        </div>
      </div>
    );
  }

  let previous: Message | null = null;
  let lastDay = '';

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
    {/* Reading older messages: new ones do not yank you down; this says they
        are there, and one press takes you to them. */}
    {newBelow > 0 ? (
      <button
        type="button"
        onClick={jumpToNewest}
        className="absolute bottom-3 left-1/2 z-[2] flex -translate-x-1/2 items-center gap-2 rounded-full bg-accent px-4 py-2 text-[13.5px] font-semibold text-accent-fg shadow-[var(--shadow-2)] outline-none transition-[transform,opacity] duration-200 hover:bg-accent-hi focus-visible:shadow-[0_0_0_3px_var(--accent-soft)] animate-[rise_220ms_var(--ease-out-soft)]"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6" /></svg>
        {newBelow === 1 ? 'New message' : newBelow + ' new messages'}
      </button>
    ) : null}
    <div
      ref={ref}
      onScroll={onScroll}
      onMouseUp={(e) => {
        /* A plain click in the log - not selecting text, not on a control -
           goes to the message box, so you can start typing straight away. */
        const t = e.target as HTMLElement;
        if (e.button !== 0 || window.getSelection()?.toString()) return;
        if (t.closest('button, a, input, textarea, select, img, video, audio, [role="button"], [data-no-focus]')) return;
        focusComposer();
      }}
      className="min-h-0 flex-1 overflow-y-auto px-[var(--pad)] py-[var(--gap)]"
    >
      {/* The top of the loaded history. */}
      {hasOlder ? (
        <div className="grid h-9 place-items-center text-[11.5px] text-ghost">
          {loadingOlder ? 'Loading earlier messages…' : 'Scroll up for more'}
        </div>
      ) : null}

      {messages.map((msg) => {
        const day = new Date(msg.createdAt).toDateString();
        const newDay = day !== lastDay;
        if (newDay) lastDay = day;

        const grouped =
          !newDay &&
          !!previous &&
          previous.userId === msg.userId &&
          previous.kind !== 'system' &&
          msg.kind !== 'system' &&
          msg.createdAt - previous.createdAt < GROUP_WINDOW;

        // System lines break a run without becoming one themselves.
        previous = msg.kind === 'system' ? null : msg;

        return (
          <div key={msg.id}>
            {newDay ? <DaySeparator at={msg.createdAt} /> : null}
            {msg.kind === 'system' ? (
              <SystemLine msg={msg} />
            ) : msg.kind === 'announce' ? (
              <Announcement msg={msg} />
            ) : (
              <Row
                msg={msg}
                grouped={grouped}
                canModerate={canModerate}
                alwaysStamp={!!settings?.showTimestamps}
                compact={!!settings?.compactMessages}
                onReply={onReply}
                onPin={onPin}
              />
            )}
          </div>
        );
      })}
    </div>
    </div>
  );
}

function DaySeparator({ at }: { at: number }) {
  const d = new Date(at);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  const label =
    d.toDateString() === today.toDateString() ? 'Today'
      : d.toDateString() === yesterday.toDateString() ? 'Yesterday'
        : d.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="my-3 flex items-center gap-3">
      <span className="h-px flex-1 bg-line-soft" />
      <span className="text-[10.5px] font-extrabold tracking-[.1em] text-ghost uppercase">{label}</span>
      <span className="h-px flex-1 bg-line-soft" />
    </div>
  );
}

function SystemLine({ msg }: { msg: Message }) {
  return (
    <div className="flex items-center gap-2 py-1 pl-1 text-[12.5px] text-mute italic">
      <span className="h-1 w-1 rounded-full bg-ghost" />
      <span>{msg.user?.displayName ?? 'Someone'} {msg.body}</span>
    </div>
  );
}

function Row({
  msg, grouped, canModerate, alwaysStamp, compact, onReply, onPin
}: {
  msg: Message;
  grouped: boolean;
  canModerate?: boolean;
  alwaysStamp: boolean;
  compact: boolean;
  onReply?: (m: Message) => void;
  /** Only passed when you are allowed to pin in this conversation. */
  onPin?: (m: Message) => void;
}) {
  const me = useSession((s) => s.me);
  const clock24 = useSession((s) => !!s.settings?.clock24);
  const embeds = useSession((s) => s.settings?.showEmbeds !== false);
  const mine = me?.id === msg.userId;
  /* Bubbles unless the player chose compact messages in Settings. */
  /* The plain log by default - picture, name, time and the text under them.
     Bubbles are a choice in Settings > Chat. */
  const bubblesOn = useSession((s) => s.settings?.chatBubbles === true);
  const bubbles = bubblesOn && !compact;
  /* Arrived in the last few seconds, as the row first mounts: it pops in.
     Fixed at mount, so re-renders never replay the animation. */
  const [fresh] = useState(() => Date.now() - msg.createdAt < 6000);
  // Animated emoji only move while you are pointing at the message.
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false);
  // What opened the reaction picker, so it can be placed against it.
  const reactAnchor = useRef<HTMLElement | null>(null);
  const [reporting, setReporting] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const { confirm, dialog } = useConfirm();
  const toast = useToast();
  const reactNow = useReact();

  /* Named in this message, by id or by @username in the text. */
  const meId = useSession((st) => st.me?.id);
  const myName = useSession((st) => st.me?.username);
  const mentionsMe = !!(
    (meId && msg.mentions?.includes(meId))
    // Escaped: in a plain string '\b' is a backspace character, not a word
    // boundary, so this half of the test never matched anything.
    || (myName && new RegExp('@' + myName + '\\b', 'i').test(msg.body || ''))
  );

  /*
   * A message of mine that named somebody else.
   *
   * Marked with the same stroke down the side, because it is the same kind of
   * thing - a message that singled a person out - but without the tinted
   * background. The tint means "this one is about you", and your own message
   * never is.
   */
  const iMentioned = !!(
    meId && msg.userId === meId && msg.mentions?.some((id) => id !== meId)
  );

  const time = new Date(msg.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: !clock24 });

  /*
   * Keys, on whichever message you are pointing at.
   *
   * The same four things the hover toolbar offers, without the trip to it.
   * Bare letters, because pointing at a message is already the modifier -
   * and the guard below means they never fire while you are typing, which is
   * the only time a bare letter could be the wrong answer.
   */
  useEffect(() => {
    if (!hovered || editing) return;

    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (inTextField(e.target)) return;

      switch (e.key) {
        case 'r': case 'R':
          if (!onReply) return;
          onReply(msg);
          break;
        case 'e': case 'E':
          if (!mine || msg.kind === 'system') return;
          setEditing(true);
          break;
        case 'Delete': case 'Backspace':
          if (!mine && !canModerate) return;
          void remove();
          break;
        case '+': case '=':
          setPicking(true);
          break;
        default:
          return;
      }
      e.preventDefault();
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  async function remove() {
    const ok = await confirm(
      'Delete this message?',
      (msg.body ? `"${msg.body.slice(0, 90)}${msg.body.length > 90 ? '…"' : '"'}` : 'The attachment goes with it.') +
      ' This cannot be undone.',
      { confirmLabel: 'Delete', danger: true }
    );
    if (!ok) return;

    /*
     * Gone from the log immediately, then confirmed with the server.
     *
     * Waiting for the round trip meant a message you had already decided to
     * delete sat there for a moment looking like the click had missed.
     */
    const wait = burstDelay('delete');
    if (wait) {
      toast('Deleting a lot at once - one moment', 'warn');
      await new Promise((r) => setTimeout(r, wait));
    }

    const node = document.getElementById('msg-' + msg.id);
    if (node) node.style.display = 'none';

    try {
      await api.del('/api/messages/' + msg.id);
    } catch (err) {
      // It is still there after all - put it back rather than leaving a hole.
      if (node) node.style.display = '';
      toast((err as Error).message, 'bad');
    }
  }

  const menu = useContextMenu(() => {
    if (msg.kind === 'system') return [];
    const items: MenuItem[] = [
      { label: 'Add reaction', icon: 'sparkle', onClick: () => setPicking(true) }
    ];
    if (onReply) items.push({ label: 'Reply', icon: 'reply', onClick: () => onReply(msg) });
    items.push({ label: 'Forward', icon: 'send', onClick: () => setForwarding(true) });
    if (msg.body) {
      items.push({
        label: 'Copy text',
        icon: 'copy',
        onClick: () => {
          void copyText(msg.body);
          toast('Copied', 'good');
        }
      });
    }
    items.push({ label: 'Copy link', icon: 'link', onClick: () => {
      void copyText(location.href.split('?')[0] + '#msg-' + msg.id);
      toast('Link copied', 'good');
    } });
    /* Pinning was only ever on the hover toolbar, which does not exist on a
       touch screen and is easy to miss on a mouse - so the action people
       looked for in the menu was the one action not in it. */
    if (onPin) {
      items.push({
        label: msg.pinned ? 'Unpin' : 'Pin this message',
        icon: 'pin',
        onClick: () => onPin(msg)
      });
    }
    /* Pinning is for the room; this is for you, and nobody is told. */
    items.push({
      label: 'Save this message',
      icon: 'bookmark',
      onClick: () => {
        void api.post('/api/messages/' + msg.id + '/save', { saved: true })
          .then(() => toast('Saved. Find it under Saved messages.', 'good'))
          .catch((err) => toast((err as Error).message, 'bad'));
      }
    });
    if (mine) {
      items.push('-');
      items.push({ label: 'Edit message', icon: 'edit', onClick: () => setEditing(true) });
    }
    if (mine || canModerate) {
      if (!mine) items.push('-');
      items.push({ label: 'Delete message', icon: 'trash', danger: true, onClick: () => void remove() });
    }
    if (!mine) {
      items.push('-');
      items.push({ label: 'Report message', icon: 'flag', onClick: () => setReporting(true) });
    }
    return items;
  });

  return (
    <div
      id={'msg-' + msg.id}
      {...menu.props}
      /*
       * Mouse only.
       *
       * A touch screen fires pointerenter on tap and never fires the matching
       * pointerleave, so every message you so much as brushed past kept its
       * toolbar open - a screen of stacked, overlapping toolbars. On a phone
       * nothing is revealed by pointing at it; you hold a message to get its
       * menu, which is one at a time by construction.
       */
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHovered(true); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHovered(false); }}
      onPointerCancel={() => setHovered(false)}
      className={cn(
        'chat-row group relative flex gap-3 rounded-[calc(8px*var(--radius-mul,1))] px-1 transition-colors hover:bg-[color-mix(in_srgb,var(--accent)_5%,transparent)]',
        /* Everyone on the left, yourself included, the way Discord reads:
           picture, name, then the bubble. Split left and right, a group chat
           turned into a zigzag and your own name vanished from the log. */
        'target:bg-[color-mix(in_srgb,var(--accent)_16%,transparent)]',
        // A run from one person sits tight; the break comes before a new
        // name, which is what the eye uses to tell one person from the next.
        grouped ? 'py-[1px]' : compact ? 'py-0.5' : 'py-[1px]',
        // Named in it. The row is marked whether or not you were looking at
        // the conversation when it arrived - a notification you were already
        // reading past is no use at all.
        !grouped && !compact && 'msg-break',
        mentionsMe && 'bg-[color-mix(in_srgb,var(--accent)_9%,transparent)] shadow-[inset_2px_0_0_var(--accent)]',
        !mentionsMe && iMentioned && 'shadow-[inset_2px_0_0_color-mix(in_srgb,var(--accent)_55%,transparent)]',
      )}
    >
      <span className="relative w-[40px] flex-none">
        {grouped ? (
          /*
           * Out of the flow entirely.
           *
           * As a block in the gutter this stretched to the row and set its
           * height - so a one-line message was 38px tall for the sake of a
           * timestamp that is invisible until you hover. Positioned, it
           * costs nothing and the text decides how tall the line is.
           */
          <span className={cn('pointer-events-none absolute inset-x-0 top-[2px] h-[16px] overflow-visible text-center text-[10.5px] leading-[16px] whitespace-nowrap text-ghost tabular-nums', alwaysStamp ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')}>
            {time}
          </span>
        ) : msg.user ? (
          <button
            type="button"
            aria-label={'Show ' + msg.user!.displayName + "'s profile card"}
            onClick={(e) => { e.stopPropagation(); openUserCard(msg.user!, e.currentTarget); }}
          >
            <Avatar user={msg.user} size={40} />
          </button>
        ) : null}
      </span>

      <div className={cn('min-w-0 flex-1', bubbles && 'flex flex-col', bubbles && 'items-start')}>
        {/* Forwarded. Small, grey and capital, above the words themselves -
            it is a note about where this came from, not part of what was
            said. */}
        {msg.forwardedFrom ? (
          <span className="mb-0.5 flex items-center gap-1.5 text-[10px] font-bold tracking-[.08em] text-ghost uppercase">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" className="flex-none -scale-x-100">
              <path d="M9 7 4 12l5 5M4 12h9a6 6 0 0 1 6 6v1" />
            </svg>
            Forwarded
            <span className="text-mute">from {msg.forwardedFrom.name}</span>
          </span>
        ) : null}

        {msg.replyTo ? (
          <button
            type="button"
            // Jumps to the message being answered and flashes it, so a reply
            // in a long thread is one click from its context.
            onClick={() => jumpTo(msg.replyTo!.id)}
            className="mb-1 flex w-full items-center gap-1.5 text-left text-[12px] text-mute transition-colors hover:text-dim"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-none">
              <path d="M9 7 4 12l5 5M4 12h9a6 6 0 0 1 6 6v1" />
            </svg>
            <span className="font-semibold text-dim">{msg.replyTo.name}</span>
            <span className="min-w-0 truncate">
              {msg.replyTo.body || (msg.replyTo.hasAttachment ? 'attachment' : '')}
            </span>
          </button>
        ) : null}
        {!grouped && msg.user ? (
          <div className={cn('flex flex-wrap items-center gap-2', bubbles && 'mb-0.5')}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openUserCard(msg.user!, e.currentTarget); }}
              className="hover:underline"
            >
              <UserName user={msg.user} className="text-[15px] font-bold" />
            </button>
            {/* Nothing but the role marker here. Badges and tier tags are
                profile furniture; in a log they turn every line into stickers. */}
            <RoleTag role={msg.user.role} />
            {msg.user ? <CrewTag crew={msg.user.crew} /> : null}
            <span className="flex-none text-[11.5px] whitespace-nowrap text-ghost tabular-nums">{time}</span>
            {msg.edited ? <span className="text-[11px] text-ghost">(edited)</span> : null}
          </div>
        ) : null}

        {editing ? (
          <EditBox msg={msg} onDone={() => setEditing(false)} />
        ) : (
          <>
            {msg.body ? (
              /*
               * Bubbles, as in the reference: yours in the accent colour on
               * the right, everyone else's on the left. A message that is only
               * emoji is drawn large without one. Compact messages (Settings)
               * keep the plain log for people who prefer density.
               */
              <p
                className={cn(
                  'selectable text-[length:var(--chat-fs,16px)] leading-[1.5] break-words whitespace-pre-wrap',
                  /* Evenly rounded all round - the sharp "tail" corner read
                     as a mistake rather than a speech bubble. */
                  bubbles && !isOnlyEmoji(msg.body) && 'max-w-[min(620px,100%)] rounded-[calc(16px*var(--radius-mul,1))] px-4 py-2.5',
                  bubbles && !isOnlyEmoji(msg.body) && (mine
                    ? 'bg-accent text-accent-fg shadow-[0_4px_14px_-6px_color-mix(in_srgb,var(--accent)_70%,transparent)] [&_a]:text-accent-fg [&_a]:underline'
                    /* The top surface step: the lower ones sit within a few
                       shades of the ivory card and the bubble all but vanished. */
                    : 'bg-surface-4 text-ink shadow-[0_1px_2px_rgba(0,0,0,.06)]'),
                  bubbles && grouped && 'mt-1',
                  bubbles && fresh && 'msg-pop-left'
                )}
              >
                <Body text={msg.body} playing={hovered} />
                {grouped && msg.edited ? (
                  <span className="ml-1.5 text-[11px] text-ghost">(edited)</span>
                ) : null}
              </p>
            ) : null}
            {msg.poll ? <PollCard msg={msg} /> : null}
            {msg.body && msg.kind !== 'system' ? <Translated msg={msg} /> : null}
            {msg.replyCount ? <ThreadLink msg={msg} /> : null}
            <Attachments list={msg.attachments ?? []} msg={msg} />
            {msg.body && embeds ? <PartyEmbed body={msg.body} /> : null}
            <Reactions msg={msg} />
          </>
        )}
      </div>

      {!editing ? (
        <div
          className={cn(
            'absolute -top-3 hidden items-center gap-0.5 rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-2 p-1 shadow-[var(--shadow-2)] group-hover:flex',
            /* On the empty side, so it never covers the bubble you point at. */
            'right-2'
          )}
          data-keeps-card
        >
          <EmojiButton msg={msg} />
          {onReply ? (
            <IconButton label="Reply" className="h-7 w-7" onClick={() => onReply(msg)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                <path d="M20 12H5M11 18l-6-6 6-6" />
              </svg>
            </IconButton>
          ) : null}
          {onPin ? (
            <IconButton
              label={msg.pinned ? 'Unpin' : 'Pin this message'}
              className="h-7 w-7"
              onClick={() => onPin(msg)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                <path d="M9 4h6l-1 6 3.5 3H6.5L10 10Z" /><path d="M12 13v7" />
              </svg>
            </IconButton>
          ) : null}
          {mine ? (
            <IconButton label="Edit" className="h-7 w-7" onClick={() => setEditing(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                <path d="M4 20h4l10-10-4-4L4 16v4ZM14.5 5.5l4 4" />
              </svg>
            </IconButton>
          ) : null}
          {mine || canModerate ? (
            <IconButton
              label="Delete"
              className="h-7 w-7 hover:bg-[color-mix(in_srgb,var(--bad)_18%,transparent)] hover:text-bad"
              onClick={() => void remove()}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                <path d="M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13" />
              </svg>
            </IconButton>
          ) : null}

          {/* Forwarding is a thing you do to a message you are looking at, so
              it belongs on the toolbar rather than only in a right-click. */}
          <IconButton label="Forward" className="h-7 w-7" onClick={() => setForwarding(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
              <path d="M4 12h15M13 6l6 6-6 6" />
            </svg>
          </IconButton>

          {/* Everything else, without having to know that right-click works.
              Same menu, opened from the button rather than the pointer. */}
          <IconButton
            label="More"
            className="h-7 w-7"
            onClick={(e) => {
              const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
              menu.openAt(box.right, box.bottom + 4);
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
            </svg>
          </IconButton>
        </div>
      ) : null}
      {dialog}
      {menu.menu}
      {picking ? (
        <EmojiPicker
            anchor={reactAnchor.current}
            onClose={() => setPicking(false)}
            onPick={(text) => {
              setPicking(false);
              reactNow(msg.id, text);
            }}
          />
      ) : null}
      {forwarding ? <Forward msg={msg} onClose={() => setForwarding(false)} /> : null}
      {reporting ? <ReportMessage msg={msg} onClose={() => setReporting(false)} /> : null}
    </div>
  );
}

/** Links become links, :name: becomes a custom emoji, everything else stays text. */
/**
 * Text with the spoilers still covered.
 *
 * ||like this||, which is the markup every chat app has settled on. Covered
 * until clicked, one span at a time, so a message can name the twist and the
 * thing it spoils without ruining either.
 */
function Spoiler({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      aria-label={open ? 'Spoiler, shown' : 'Show spoiler'}
      title={open ? 'Hide it again' : 'Click to reveal'}
      onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      className={cn(
        'relative inline rounded-[6px] px-1.5 align-baseline outline-none',
        'transition-[background-color,color,box-shadow,filter] duration-[220ms] ease-[var(--ease-out)]',
        'focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        open
          /* Revealed: a quiet tint, so you can still see what was covered. */
          ? 'bg-accent-soft text-ink'
          /*
             Covered: the app's own surface, not a black bar - the words are
             blurred rather than hidden, so the shape of the sentence stays
             and it reads as something withheld rather than something broken.

             Under the pointer it leans towards you rather than lighting up:
             the blur eases back a little - not enough to read, enough to see
             there are words under there - the edge takes the accent, and the
             word SPOILER is replaced by what pressing will do. Nothing moves
             and nothing resizes, so a spoiler in the middle of a sentence
             does not shove the rest of the line about.
          */
          : cn(
              'group/spoiler cursor-pointer select-none bg-surface-3 text-transparent',
              'shadow-[inset_0_0_0_1px_var(--line)]',
              'hover:bg-surface-4 hover:shadow-[inset_0_0_0_1px_var(--accent-line)]'
            )
      )}
    >
      <span
        className={cn(
          'transition-[filter,opacity] duration-[260ms] ease-[var(--ease-out)]',
          open ? '' : 'pointer-events-none blur-[5px] opacity-70 group-hover/spoiler:blur-[3.5px] group-hover/spoiler:opacity-90'
        )}
      >
        {text}
      </span>
      {!open ? (
        <span aria-hidden className="absolute inset-0 grid place-items-center">
          <span className="text-[10px] font-bold tracking-[.1em] text-mute uppercase transition-opacity duration-[160ms] group-hover/spoiler:opacity-0">
            spoiler
          </span>
          <span className="absolute inset-0 grid place-items-center gap-1 text-[10px] font-bold tracking-[.06em] text-accent uppercase opacity-0 transition-opacity duration-[160ms] group-hover/spoiler:opacity-100">
            <span className="flex items-center gap-1">
              <Icon name="eye" size={11} />
              reveal
            </span>
          </span>
        </span>
      ) : null}
    </button>
  );
}

function Body({ text, playing }: { text: string; playing?: boolean }) {
  // Both come out in one pass, so a token sitting inside a URL is not mistaken
  // for an emoji.
  const parts = text.split(/(\|\|[^|]+\|\||https?:\/\/[^\s<]+|:[a-z0-9_-]{2,32}:)/gi);

  /*
   * A message that is nothing but emoji is drawn large.
   *
   * Three emoji and nothing else is a reaction, not a sentence - every chat
   * app draws those big, and at body size they read as punctuation you have
   * to squint at.
   */
  const big = isOnlyEmoji(text);

  return (
    // Size comes from Appearance rather than being fixed at 34px.
    <span className={big ? 'leading-[1.25] text-[length:var(--emoji-big,34px)]' : undefined}>
      {parts.map((part, i) =>
        /^\|\|[^|]+\|\|$/.test(part) ? (
          <Spoiler key={i} text={part.slice(2, -2)} />
        ) : /^https?:\/\//.test(part) ? (
          <a
            key={i}
            href={part}
            onClick={(e) => { e.preventDefault(); openExternal(part); }}
            className="text-accent-hi hover:underline"
          >
            {part}
          </a>
        ) : /^@[a-z0-9_.-]{2,32}$/i.test(part) ? (
          <Mention key={i} text={part} />
        ) : (
          <Token key={i} text={part} big={big} playing={playing} />
        )
      )}
    </span>
  );
}

/**
 * A clip, with the notes people have pinned to moments in it.
 *
 * The player is held here rather than inside the attachment so a note can
 * move it: clicking "1:24" should take you to 1:24, which is the whole point
 * of writing the time down.
 */
function ClipVideo({ a, msg }: { a: NonNullable<Message['attachments']>[number]; msg: Message }) {
  const video = useRef<HTMLVideoElement>(null);
  return (
    <div>
      <video
        ref={video}
        src={absolute(a.url)}
        controls
        preload="metadata"
        className="block max-h-[360px] w-full rounded-[calc(16px*var(--radius-mul,1))] bg-black"
      />
      <ClipMarks msg={msg} attachment={a} video={video} />
    </div>
  );
}

/**
 * A crew announcement.
 *
 * Drawn as a notice rather than a paragraph, because the whole point of one
 * is that it is not the same as the sentence above it. The first line is its
 * title; everything after is the body.
 */
function Announcement({ msg }: { msg: Message }) {
  const [title, ...rest] = (msg.body || '').split('\n');
  return (
    <div className="my-2 rounded-[calc(16px*var(--radius-mul,1))] bg-[color-mix(in_srgb,var(--accent)_10%,var(--bg-1))] p-3.5 shadow-[inset_0_0_0_1px_var(--accent-line)]">
      <div className="flex items-center gap-2">
        <Icon name="megaphone" size={16} className="flex-none text-accent" />
        <p className="m-0 min-w-0 flex-1 text-[14.5px] font-bold text-ink">{title}</p>
        {msg.user ? <span className="flex-none text-[11.5px] text-mute">{msg.user.displayName}</span> : null}
      </div>
      {rest.length ? (
        <p className="m-0 mt-1.5 text-[13.5px] leading-relaxed whitespace-pre-wrap text-dim">{rest.join('\n')}</p>
      ) : null}
    </div>
  );
}

/**
 * The line a thread leaves in the room.
 *
 * Once a message has two answers, they are drawn in the thread rather than
 * interleaved with everything else - so this line is all the room shows, and
 * it has to be worth reading on its own: how many, who spoke last, when, and
 * whether you are following what happens next.
 */
function ThreadLink({ msg }: { msg: Message }) {
  const last = msg.lastReplyAt ? ago(msg.lastReplyAt) : '';
  return (
    <button
      type="button"
      onClick={() => openThread(msg.id)}
      className={cn(
        'mt-1.5 flex max-w-[440px] items-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] px-2 py-1.5 text-left outline-none',
        'bg-surface-2 shadow-[inset_0_0_0_1px_var(--line-soft)] transition-colors hover:bg-surface-3',
        'focus-visible:shadow-[0_0_0_2px_var(--accent)]'
      )}
    >
      {msg.lastReplyBy ? <Avatar user={msg.lastReplyBy} size={18} /> : <Icon name="chat" size={13} className="text-accent" />}
      <span className="text-[12.5px] font-bold text-accent">
        {msg.replyCount} {msg.replyCount === 1 ? 'reply' : 'replies'}
      </span>
      <span className="min-w-0 flex-1 truncate text-[11.5px] text-mute">
        {msg.lastReplyBy ? 'last from ' + msg.lastReplyBy.displayName : 'in a thread'}{last ? ' · ' + last : ''}
      </span>
      {msg.following ? <Icon name="bell" size={12} className="flex-none text-accent" title="You are following this thread" /> : null}
    </button>
  );
}

/**
 * True when a message is only emoji and whitespace.
 *
 * Counted rather than pattern-matched at the ends, so "ok 👍" stays normal
 * size and "👍👍👍" does not. Capped: a wall of thirty is a picture, and at
 * 34px each it would fill the window.
 */
function isOnlyEmoji(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;

  // Custom :name: tokens count as emoji, so stand them in for one glyph each
  // before looking at what is left.
  let customCount = 0;
  const rest = trimmed.replace(/:[a-z0-9_-]{2,32}:/gi, () => { customCount++; return ''; });

  const glyphs = [...rest.replace(/\s/g, '')];
  if (!glyphs.length && !customCount) return false;
  // A wall of them is a picture; at 34px each it would fill the window.
  if (glyphs.length + customCount > 8) return false;

  return glyphs.every((c) => /\p{Extended_Pictographic}|\p{Emoji_Component}/u.test(c));
}

/**
 * A name somebody typed with an @ in front of it.
 *
 * Lit only when it points at a real account, so a stray email address or a
 * price does not turn into a link to nobody. Your own name is brighter, which
 * is the whole reason to look at a mention.
 */
function Mention({ text }: { text: string }) {
  const me = useSession((s) => s.me);
  const name = text.slice(1).toLowerCase();
  const isMe = me?.username.toLowerCase() === name;

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); go('u/' + name); }}
      className={cn(
        'rounded-[calc(6px*var(--radius-mul,1))] px-1 font-semibold transition-colors',
        isMe
          ? 'bg-[color-mix(in_srgb,var(--accent)_28%,transparent)] text-ink'
          : 'bg-accent-soft text-accent-hi hover:bg-[color-mix(in_srgb,var(--accent)_22%,transparent)]'
      )}
    >
      {text}
    </button>
  );
}

/** A run of plain text, or one custom emoji if the token names a real one. */
function Token({ text, big, playing }: { text: string; big?: boolean; playing?: boolean }) {
  // Subscribed rather than read once: the set arrives after the first paint,
  // and a module-level lookup would leave every :name: as plain text until
  // something else happened to re-render the log.
  const custom = useSession((s) => s.emoji);
  const name = /^:([a-z0-9_-]{2,32}):$/i.exec(text)?.[1];
  const emoji = name ? custom.find((e) => e.name.toLowerCase() === name.toLowerCase()) : null;
  if (!emoji) return <span>{text}</span>;
  return <EmojiImg url={emoji.url} label={text} big={big} playing={playing} emoji={emoji} />;
}

/**
 * A party link, shown as the party.
 *
 * The chat reference draws an invite as a card - game art, the party's name
 * and seats, who is in it, and View party - rather than a bare link. Any
 * message with a link to a party gets one, live from the server, so the seat
 * count is today's and a party that has ended simply shows nothing.
 */
function PartyEmbed({ body }: { body: string }) {
  const id = /#\/party\/(p_[a-z0-9]+)/i.exec(body)?.[1] ?? null;
  const catalog = useSession((s) => s.catalog);
  const q = useQuery({
    queryKey: ['party', id],
    queryFn: () => api.get<{ party: Party }>('/api/parties/' + id),
    enabled: !!id,
    staleTime: 30_000,
    retry: false
  });
  const party = q.data?.party;
  if (!id || !party || party.closed) return null;

  const game = catalog?.games.find((g) => g.id === party.gameId);
  const art = gameBanner(party.gameId);
  const people = party.members.map((m) => m.user).filter((u): u is PublicUser => !!u);

  return (
    <div className="pugo-panel mt-2 flex w-full max-w-[540px] flex-wrap items-center gap-4 rounded-[calc(18px*var(--radius-mul,1))] border border-line-soft bg-surface-1 p-4 shadow-[var(--shadow-1)]">
      <span className="h-[72px] w-[72px] flex-none overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] bg-surface-3">
        {art ? <img src={art} alt="" className="h-full w-full object-cover" /> : null}
      </span>
      <span className="min-w-[160px] flex-1">
        <span className="block truncate text-[16px] font-bold text-ink">
          {party.title} · {party.memberCount}/{party.size} players
        </span>
        <span className="block truncate text-[14px] text-mute">
          {[game?.name, party.mode, party.voice?.enabled === false ? null : 'Voice chat on'].filter(Boolean).join(' · ')}
        </span>
        <span className="mt-2 flex items-center">
          {people.slice(0, 3).map((u) => (
            <span key={u.id} className="-mr-2 rounded-full ring-2 ring-[var(--bg-1)]"><Avatar user={u} size={28} /></span>
          ))}
          {people.length > 3 ? (
            <span className="ml-3 grid h-7 min-w-7 place-items-center rounded-full bg-surface-3 px-1.5 text-[12px] font-semibold text-dim">+{people.length - 3}</span>
          ) : null}
        </span>
      </span>
      <Button kind="primary" onClick={() => go('party/' + party.id)} className="h-[46px] px-6 text-[15px]">View party</Button>
    </div>
  );
}

function Attachments({ list, msg }: { list: NonNullable<Message['attachments']>; msg?: Message }) {
  // Which picture the viewer is on, or null while it is closed.
  const [viewing, setViewing] = useState<number | null>(null);
  const toast = useToast();
  if (!list.length) return null;

  const pictures = list.filter((a) => a.image);
  /* Clips, audio and voice notes play here in the conversation; anything
     else is a file to save. */
  const videos = list.filter((a) => !a.image && a.type.startsWith('video/') && !isVoiceNote(a));
  const sounds = list.filter((a) => !a.image && (a.type.startsWith('audio/') || isVoiceNote(a)));
  const files = list.filter((a) => !a.image && !videos.includes(a) && !sounds.includes(a));

  function saveFile(a: NonNullable<Message['attachments']>[number]) {
    const link = document.createElement('a');
    link.href = absolute(a.url);
    link.download = a.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  return (
    <div className="mt-2 flex flex-col gap-2">
      {pictures.length ? (
        <div className={cn('grid max-w-[440px] gap-1.5', pictures.length > 1 && 'grid-cols-2')}>
          {pictures.map((a, i) => (
            <button
              key={a.id}
              type="button"
              // Opens over the app rather than throwing you into a browser.
              onClick={() => setViewing(i)}
              aria-label={'Open ' + a.name}
              className="group/pic smooth-clip relative overflow-hidden rounded-[calc(16px*var(--radius-mul,1))] bg-surface-2 outline-none transition-[background-color] duration-[250ms] hover:bg-surface-3 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              {/*
                The space is held before the bytes arrive (from the size the
                server read out of the file), with a placeholder while it
                loads and a clear state if it cannot - so a large picture
                never shoves the conversation away from what you are reading.
              */}
              <SafeImage
                a={a}
                src={absolute(a.url)}
                cover={pictures.length > 1}
                className={pictures.length > 1 ? 'max-h-[300px]' : 'max-h-[340px]'}
              />
              {/* A soft edge on top of the picture, and the way to open it big. */}
              <span aria-hidden className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0_0_0_1px_var(--line-soft)]" />
              <span aria-hidden className="absolute top-2.5 right-2.5 grid h-9 w-9 place-items-center rounded-[11px] bg-black/55 text-white opacity-0 backdrop-blur-sm transition-[opacity,transform] duration-[250ms] ease-[var(--ease-out)] group-hover/pic:opacity-100 group-focus-visible/pic:opacity-100">
                <Icon name="expand" size={16} />
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {/* A clip can carry notes pinned to moments in it. */}
      {videos.map((a) => (
        msg ? <ClipVideo key={a.id} a={a} msg={msg} /> : <VideoAttachment key={a.id} a={a} src={absolute(a.url)} />
      ))}
      {sounds.map((a) => (
        <div key={a.id}>
          {isVoiceNote(a)
            ? <VoiceNote a={a} src={absolute(a.url)} />
            : <AudioAttachment a={a} src={absolute(a.url)} />}
          {msg && isVoiceNote(a) ? <Transcript msg={msg} attachment={a} /> : null}
        </div>
      ))}

      {files.map((a) => (
        <div
          key={a.id}
          className="flex max-w-[440px] items-center gap-3 rounded-[var(--radius)] border border-line-soft bg-surface-2 p-2.5"
        >
          <span className="grid h-9 w-9 flex-none place-items-center rounded-md bg-surface-3 text-accent">
            <Icon name="download" size={16} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold">{a.name}</span>
            <span className="block text-[11px] text-ghost">{humanSize(a.size)}</span>
          </span>
          <IconButton label="Copy link" className="h-8 w-8" onClick={() => {
            void copyText(absolute(a.url)).then((ok) => toast(ok ? 'Link copied' : 'Could not copy', ok ? 'good' : 'bad'));
          }}>
            <Icon name="copy" size={14} />
          </IconButton>
          <IconButton label="Save" className="h-8 w-8" onClick={() => saveFile(a)}>
            <Icon name="download" size={14} />
          </IconButton>
        </div>
      ))}

      {viewing !== null ? (
        <Lightbox list={pictures} index={viewing} onClose={() => setViewing(null)} />
      ) : null}
    </div>
  );
}

function EditBox({ msg, onDone }: { msg: Message; onDone: () => void }) {
  const [value, setValue] = useState(msg.body);
  const ref = useRef<HTMLTextAreaElement>(null);
  const toast = useToast();

  // The box follows the text; dragging a resize handle around inside a chat
  // log is not something anyone wants to do.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = Math.min(node.scrollHeight, 320) + 'px';
  }, [value]);

  useEffect(() => { ref.current?.focus(); ref.current?.setSelectionRange(value.length, value.length); }, []);

  async function save() {
    const next = value.trim();
    if (next === msg.body) return onDone();
    try { await api.patch(`/api/messages/${msg.id}`, { body: next }); onDone(); }
    catch (err) { toast((err as Error).message, 'bad'); }
  }

  return (
    <div className="mt-1">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onDone(); }
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void save(); }
        }}
        className="w-full resize-none overflow-y-auto rounded-[var(--radius-s)] border border-line bg-surface-1 p-2.5 text-[14.5px] outline-none focus:border-accent"
      />
      <div className="mt-1.5 flex items-center gap-2">
        <span className="text-[11.5px] text-ghost">Enter to save, Escape to cancel</span>
        <div className="flex-1" />
        <Button size="sm" kind="ghost" onClick={onDone}>Cancel</Button>
        <Button size="sm" kind="primary" onClick={() => void save()}>Save</Button>
      </div>
    </div>
  );
}

/**
 * Reacting, without the wait.
 *
 * Paints the chip straight into the cache, then tells the server. Three
 * places offer reactions - the row menu, the chips themselves and the picker
 * - and all three go through here so none of them can drift into being the
 * slow one.
 */
function useReact() {
  const qc = useQueryClient();
  const meId = useSession((st) => st.me?.id);

  return (id: string, emoji: string) => {
    toggleReaction(qc, id, emoji, meId);
    // If it fails the next socket push or refetch puts it back; a reaction is
    // not worth a toast.
    void api.post('/api/messages/' + id + '/react', { emoji });
  };
}

/* --------------------------------------------------------- reactions */

const QUICK = ['👍', '😂', '🔥', '❤️', '💀', '👀', '🎯', '😭', '🙌', '🧠', '😮', '🫡'];

function Reactions({ msg }: { msg: Message }) {
  const reactNow = useReact();
  const me = useSession((s) => s.me);
  const entries = Object.entries(msg.reactions ?? {}).filter(([, ids]) => ids.length);
  if (!entries.length) return null;

  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {entries.map(([emoji, ids]) => {
        const mine = !!me && ids.includes(me.id);
        return (
          <button
            key={emoji}
            type="button"
            onClick={() => reactNow(msg.id, emoji)}
            className={cn(
              'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[12px] transition-colors',
              mine
                ? 'border-accent bg-[color-mix(in_srgb,var(--accent)_18%,transparent)] text-accent-hi'
                : 'border-line bg-surface-2 text-dim hover:border-accent-line hover:bg-surface-3'
            )}
          >
            <span>{emoji}</span>
            <span className="font-bold tabular-nums">{ids.length}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The quick picker. Twelve that get used, not a thousand that do not. */
function EmojiButton({ msg }: { msg: Message }) {
  const reactNow = useReact();
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(false);
  // The button itself, so the full picker opens beside it rather than
  // wherever the nearest positioned ancestor happens to be.
  const btn = useRef<HTMLSpanElement>(null);

  function react(emoji: string) {
    reactNow(msg.id, emoji);
  }

  return (
    <span ref={btn} className="relative" data-keeps-card>
      <IconButton label="Add reaction" className="h-7 w-7" onClick={() => setOpen(!open)}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <circle cx="12" cy="12" r="9" />
          <path d="M8.4 14.2a4.4 4.4 0 0 0 7.2 0" />
          <circle cx="9" cy="9.6" r="1" fill="currentColor" stroke="none" />
          <circle cx="15" cy="9.6" r="1" fill="currentColor" stroke="none" />
        </svg>
      </IconButton>

      {open ? (
        <>
          <span className="fixed inset-0 z-[80]" onClick={() => setOpen(false)} />
          <span className="absolute top-full right-0 z-[90] mt-1 w-[212px] rounded-[var(--radius)] border border-line bg-surface-2 p-1.5 shadow-[var(--shadow-2)] animate-[rise_160ms_var(--ease-out-soft)]">
            <span className="grid grid-cols-6 gap-0.5">
              {QUICK.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => { setOpen(false); react(emoji); }}
                  className="grid h-8 w-8 place-items-center rounded-md text-[17px] transition-[background,transform] hover:scale-110 hover:bg-surface-4"
                >
                  {emoji}
                </button>
              ))}
            </span>
            <button
              type="button"
              // The twelve above cover most reactions; this is the door to the
              // rest of them, and to the custom set.
              onClick={() => { setOpen(false); setFull(true); }}
              className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-md border-t border-line-soft pt-1.5 text-[11.5px] font-semibold text-mute transition-colors hover:text-ink"
            >
              <Icon name="plus" size={12} />
              All emoji
            </button>
          </span>
        </>
      ) : null}

      {full ? (
        <EmojiPicker
          anchor={btn.current}
          onClose={() => setFull(false)}
          onPick={(text) => { setFull(false); react(text); }}
        />
      ) : null}
    </span>
  );
}

/* ---------------------------------------------------------- composer */

/** A composer tool: its own soft, rounded square beside the message field. */
const TOOL = cn(
  'grid h-11 w-11 flex-none cursor-pointer place-items-center rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 text-dim outline-none',
  'shadow-[inset_0_0_0_1px_var(--line-soft)] transition-[background-color,color,box-shadow] duration-[250ms] ease-[var(--ease-out)]',
  'hover:bg-surface-3 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)] disabled:cursor-default disabled:opacity-50'
);

/*
 * Type anywhere, land in the message box.
 *
 * Like Discord: with a conversation open, pressing a letter while nothing
 * else has the keyboard puts the caret in its composer and the letter goes
 * in. The most recently shown composer is the one that answers; dialogs,
 * other text fields and shortcuts (Ctrl, Alt, Meta) are left alone.
 */
const composers: HTMLTextAreaElement[] = [];
let typeToFocusWired = false;

export function focusComposer(): boolean {
  const node = [...composers].reverse().find((c) => c.isConnected && !c.disabled && c.offsetParent !== null);
  if (!node) return false;
  node.focus({ preventScroll: true });
  const at = node.value.length;
  node.setSelectionRange(at, at);
  return true;
}

function wireTypeToFocus() {
  if (typeToFocusWired) return;
  typeToFocusWired = true;
  window.addEventListener('keydown', (e) => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
    if (e.key.length !== 1 && e.key !== 'Backspace') return;
    const t = e.target as HTMLElement | null;
    if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
    /* A dialog, menu or picker has the keyboard. */
    if (document.querySelector('[aria-modal="true"], [role="menu"], [role="listbox"]')) return;
    if (e.key === ' ' && t && (t.tagName === 'BUTTON' || t.getAttribute('role') === 'button')) return;
    focusComposer();
  }, true);
}

export function Composer({
  placeholder, onSend, onTyping, disabled, replyTo, onCancelReply, focusKey, people, poll
}: {
  placeholder?: string;
  /** Where a poll asked from here would go. Absent means no poll button. */
  poll?: { scope: 'party' | 'crew' | 'group' | 'dm'; channelId: string };
  /** Who can be mentioned here - the party's members, or the two of you. */
  people?: PublicUser[];
  /**
   * Changes when you switch conversation. The caret follows, so opening a
   * chat leaves you ready to type rather than needing a click first.
   */
  focusKey?: string;
  disabled?: boolean;
  replyTo?: Message | null;
  onCancelReply?: () => void;
  onSend: (body: string, attachments: string[], replyTo: string | null, spoilers?: string[]) => Promise<void>;
  onTyping?: () => void;
}) {
  /*
   * What you were in the middle of writing.
   *
   * Kept per conversation, on this device, and restored when you come back -
   * including after the app is closed, the connection drops, or you switch to
   * another chat to check something. Half-written messages are lost far more
   * often to a reconnect than to anybody changing their mind.
   */
  const draftKey = focusKey ? 'pogu.draft.' + focusKey : '';
  const [value, setValue] = useState(() => {
    try { return draftKey ? localStorage.getItem(draftKey) ?? '' : ''; } catch { return ''; }
  });
  const [pending, setPending] = useState<Attachment[]>([]);
  /* Files to send covered. A screenshot of the ending is worth marking. */
  const [spoilers, setSpoilers] = useState<string[]>([]);
  /* The voice message recorder, open above the composer. */
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);
  const [mention, setMention] = useState<MentionState | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  /** Files on their way up, with a bar each. */
  const [uploading, setUploading] = useState<UploadRow[]>([]);
  /* The phone attachment sheet. */
  const [attachSheet, setAttachSheet] = useState(false);
  /* Asking the room, from where the room is. */
  const [asking, setAsking] = useState(false);

  /* Switching conversation loads that conversation's draft. */
  useEffect(() => {
    if (!draftKey) return;
    try { setValue(localStorage.getItem(draftKey) ?? ''); } catch { /* storage off */ }
  }, [draftKey]);

  /* Written back a moment after typing stops, not on every keystroke. */
  useEffect(() => {
    if (!draftKey) return;
    const id = window.setTimeout(() => {
      try {
        if (value.trim()) localStorage.setItem(draftKey, value);
        else localStorage.removeItem(draftKey);
      } catch { /* storage off - the draft simply is not kept */ }
    }, 400);
    return () => window.clearTimeout(id);
  }, [draftKey, value]);

  /** Opens the system picker, narrowed to a kind of file. */
  function openPicker(accept: string) {
    const input = fileRef.current;
    if (!input) return;
    input.accept = accept;
    input.click();
  }
  /** So a row can be cancelled mid-flight. */
  const xhrs = useRef(new Map<string, XMLHttpRequest>());
  const perks = useSession((st) => st.me?.perks);
  const emojiBtn = useRef<HTMLSpanElement>(null);
  const [dragging, setDragging] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const settings = useSession((s) => s.settings);
  const toast = useToast();

  /* Registered for type-to-focus while it is on screen. */
  useEffect(() => {
    wireTypeToFocus();
    const node = ref.current;
    if (!node) return;
    composers.push(node);
    return () => { const i = composers.indexOf(node); if (i >= 0) composers.splice(i, 1); };
  }, []);

  /*
   * Opening a conversation puts the caret in the box.
   *
   * One requestAnimationFrame was not enough: the log scrolls itself to the
   * bottom in a layout effect on the same tick, and on a cold open the
   * composer is laid out before the messages arrive and again after, so the
   * single attempt often landed on a node that was about to be replaced.
   * Two goes, a frame and then a beat later, and it stops if something else
   * has since been clicked into.
   */
  useEffect(() => {
    if (!focusKey || disabled) return;

    const focus = () => {
      const node = ref.current;
      if (!node || node === document.activeElement) return;
      const active = document.activeElement;
      // Never steal it from something the person is actually using.
      if (active && active !== document.body && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) return;
      // Without preventScroll, focusing a composer below the fold scrolled
      // the whole page down to it the moment a party opened.
      node.focus({ preventScroll: true });
      // Caret at the end of whatever draft was already there.
      const at = node.value.length;
      node.setSelectionRange(at, at);
    };

    const frame = requestAnimationFrame(focus);
    const timer = window.setTimeout(focus, 140);
    return () => { cancelAnimationFrame(frame); clearTimeout(timer); };
  }, [focusKey, disabled]);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = Math.min(node.scrollHeight, 180) + 'px';
  }, [value]);

  /**
   * Sending files, visibly.
   *
   * This used to be a fetch with no feedback at all: you picked a 200 MB
   * clip, nothing appeared, and the only way to know it was working was to
   * wait. XHR is here for one reason - it reports upload progress, and fetch
   * still cannot. Each file gets a row with a bar the moment you pick it.
   */
  async function upload(picked: File[]) {
    const limit = perks?.limits?.upload ?? 25 * 1024 * 1024;

    /*
     * Resized before the size check, not after.
     *
     * A 12 MB photo off a phone is under the limit once it is the size a
     * chat message needs, and refusing it before trying would be refusing
     * something we could perfectly well send.
     */
    const files = await Promise.all(picked.slice(0, 6).map(shrinkImage));

    for (const file of files) {
      if (file.size > limit) {
        toast(file.name + ' is ' + mb(file.size) + '. Your limit is ' + mb(limit) + '.', 'warn');
        continue;
      }
      /* The same file dropped twice while the first is still going up is
         one attachment, not two. */
      if (uploading.some((u) => u.name === file.name && u.size === file.size && !u.failed)) continue;

      const key = 'up_' + Math.random().toString(36).slice(2);
      setUploading((list) => [...list, { key, name: file.name, size: file.size, percent: 0, file }]);
      startUpload(file, key);
    }
  }

  /** Try a failed upload again, in the same row. */
  function retryUpload(key: string) {
    const row = uploading.find((u) => u.key === key);
    if (!row) return;
    setUploading((list) => list.map((u) => (u.key === key ? { ...u, percent: 0, failed: undefined } : u)));
    startUpload(row.file, key);
  }

  function startUpload(file: File, key: string) {
    {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', serverUrl() + '/api/files');
      xhr.setRequestHeader('Authorization', 'Bearer ' + (localStorage.getItem('pogu.token') ?? ''));
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.setRequestHeader('X-File-Type', file.type || 'application/octet-stream');
      xhr.setRequestHeader('X-File-Name', encodeURIComponent(file.name));
      xhr.setRequestHeader('X-File-Kind', 'attachment');

      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploading((list) => list.map((u) => (u.key === key ? { ...u, percent } : u)));
      };

      const done = () => { xhrs.current.delete(key); setUploading((list) => list.filter((u) => u.key !== key)); };
      /* A failure keeps its row, with the reason and a Retry, instead of
         vanishing into a toast you might not have seen. */
      const failed = (reason: string) => {
        xhrs.current.delete(key);
        setUploading((list) => list.map((u) => (u.key === key ? { ...u, failed: reason } : u)));
      };

      xhr.onload = () => {
        let data: { file?: Attachment; error?: string } = {};
        try { data = JSON.parse(xhr.responseText) as typeof data; } catch { /* not JSON */ }
        if (xhr.status >= 400 || !data.file) {
          failed(data.error ?? 'Could not upload');
          return;
        }
        done();
        const file = data.file;
        setPending((list) => (list.some((p) => p.id === file.id) ? list : [...list, file]));
      };

      // A dropped connection mid-upload, which on a big file is the likely
      // failure and used to leave the row spinning forever.
      xhr.onerror = () => failed('Connection lost');
      xhr.onabort = () => done();

      xhr.send(file);
      xhrs.current.set(key, xhr);
    }
  }

  function cancelUpload(key: string) {
    const xhr = xhrs.current.get(key);
    xhrs.current.delete(key);
    if (xhr) xhr.abort();
    /* A failed row has no request left to abort; cancelling just clears it. */
    setUploading((list) => list.filter((u) => u.key !== key));
  }
  /**
   * Sending, once.
   *
   * This used to be re-entrant, and holding Enter down proved it: every
   * repeat called submit again, each call read the same `value` because the
   * box is not cleared until after the cooldown wait, and each one sent it.
   * One press of a held key became four copies of the same message, and the
   * burst limiter made it worse - every call recorded another hit, so the
   * cooldown it eventually imposed released a queue of duplicates all at
   * once.
   *
   * A ref rather than the busy state, because state is a render behind: two
   * Enter presses in the same frame both see busy === false.
   */
  const sending = useRef(false);

  /** When the burst cooldown ends, for the countdown on the composer. */
  const [coolUntil, setCoolUntil] = useState(0);
  const [coolLeft, setCoolLeft] = useState(0);

  useEffect(() => {
    if (!coolUntil) { setCoolLeft(0); return; }
    const tick = () => setCoolLeft(Math.max(0, Math.ceil((coolUntil - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
  }, [coolUntil]);

  async function submit() {
    if (sending.current) return;

    const body = value.trim();
    if (!body && !pending.length) return;

    sending.current = true;
    setBusy(true);

    /* Taken out of the box straight away. The one send that is in flight owns
       this text now, and anything typed while it goes is the next message. */
    const attachments = pending.map((p) => p.id);
    const covered = spoilers.filter((id) => attachments.includes(id));
    setValue('');
    /* Sent means there is nothing half-written to come back to. */
    try { if (draftKey) localStorage.removeItem(draftKey); } catch { /* storage off */ }
    setPending([]);
    setSpoilers([]);
    // Keep the caret in the box. Sending should not cost you the cursor -
    // otherwise every message means clicking back into the composer before
    // you can type the next one.
    ref.current?.focus();

    try {
      /* A burst waits rather than being refused by the server after the fact,
         which loses the message. Recorded exactly once per send now, so the
         limiter counts messages instead of key repeats. */
      const wait = burstDelay('send');
      if (wait) {
        setCoolUntil(Date.now() + wait);
        await new Promise((r) => setTimeout(r, wait));
      }
      await onSend(body, attachments, replyTo?.id ?? null, covered);
      onCancelReply?.();
    } catch (err) {
      setValue(body);   // hand the text back rather than losing it
      toast((err as Error).message, 'bad');
    } finally {
      setCoolUntil(0);
      sending.current = false;
      setBusy(false);
      ref.current?.focus();
    }
  }

  const enterSends = settings?.enterToSend !== false;
  /* The live list, so an icon added while you are typing resolves at once. */
  const customEmoji = useSession((s) => s.emoji);

  /*
   * What you are about to send, when it has an icon in it.
   *
   * A textarea draws text and nothing else, so :hype: stayed as six
   * characters right up until you pressed send and found out whether you had
   * spelled it right. This line sits above the box only while there is
   * something to resolve, and shows the message the way the room will get it.
   *
   * Only the icons are worth drawing here, so it says nothing while you are
   * typing an ordinary sentence and nothing at all on a server with no custom
   * icons on it.
   */
  const iconNames = customEmoji.length ? (value.match(/:[a-z0-9_-]{2,32}:/gi) ?? []) : [];
  const resolves = iconNames.some((t) => {
    const n = t.slice(1, -1).toLowerCase();
    return customEmoji.some((e) => e.name.toLowerCase() === n);
  });

  return (
    <div className="@container flex-none px-[var(--pad)] pb-[var(--gap)]">
      {resolves ? (
        <p className="m-0 mb-1.5 flex min-w-0 items-center gap-1.5 px-1 text-[12px] text-mute animate-[fade-in_140ms_var(--ease-out)]">
          <span className="flex-none text-ghost">Sends as</span>
          <EmojiText text={value} size={16} className="min-w-0 truncate text-dim" />
        </p>
      ) : null}
      {mention && people?.length ? (
        <MentionBox
          people={people}
          state={mention}
          onClose={() => setMention(null)}
          onPick={(u) => {
            // Replace exactly the @run the caret was in, and leave a space
            // so the next word does not run into the name.
            const before = value.slice(0, mention.from) + '@' + u.username + ' ';
            const after = value.slice(mention.from + 1 + mention.query.length);
            setValue(before + after);
            setMention(null);
            requestAnimationFrame(() => {
              const box = ref.current;
              if (!box) return;
              box.focus();
              box.setSelectionRange(before.length, before.length);
            });
          }}
        />
      ) : null}

      {/* Small, grey, and out of the way. It is a note about what you are
          about to do, not a thing to look at - it used to be a bordered card
          the same weight as the composer under it. */}
      {replyTo ? (
        <div className="mb-1.5 flex items-center gap-2 px-1 text-[10.5px] animate-[rise_160ms_var(--ease-out-soft)]">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="flex-none text-ghost">
            <path d="M9 7 4 12l5 5M4 12h9a6 6 0 0 1 6 6v1" />
          </svg>
          <span className="font-bold tracking-[.08em] text-ghost uppercase">Replying to</span>
          <span className="font-bold tracking-[.08em] text-mute uppercase">
            {replyTo.user?.displayName ?? 'someone'}
          </span>
          <span className="min-w-0 flex-1 truncate text-ghost normal-case">{replyTo.body}</span>
          <IconButton label="Cancel reply" className="h-5 w-5 flex-none" onClick={onCancelReply}>
            <svg width="12" height="12" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" fill="none">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </IconButton>
        </div>
      ) : null}
      {/* On their way up. A big file takes a while and this is the only
          thing that says it is happening at all. */}
      {uploading.length ? (
        <div className="mb-2 flex flex-col gap-1.5">
          {uploading.map((u) => (
            <div
              key={u.key}
              role={u.failed ? 'alert' : undefined}
              className={cn(
                'relative flex items-center gap-2 overflow-hidden rounded-[var(--radius-s)] border bg-surface-2 px-3 py-1.5 text-[12.5px]',
                u.failed ? 'border-[color-mix(in_srgb,var(--bad)_45%,var(--line))]' : 'border-line'
              )}
            >
              {u.failed ? null : (
                <span
                  aria-hidden
                  className="absolute inset-y-0 left-0 bg-accent-soft transition-[width] duration-150"
                  style={{ width: u.percent + '%' }}
                />
              )}
              <span className="relative max-w-[180px] truncate">{u.name}</span>
              <span className="relative text-ghost">{mb(u.size)}</span>
              <span className="relative flex-1" />
              {u.failed ? (
                <>
                  <span className="relative truncate font-semibold text-bad">{u.failed}</span>
                  <button
                    type="button"
                    onClick={() => retryUpload(u.key)}
                    className="relative h-6 rounded-[8px] bg-surface-3 px-2 text-[12px] font-bold text-ink outline-none hover:bg-surface-4 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
                  >
                    Retry
                  </button>
                </>
              ) : (
                <span className="relative font-semibold tabular-nums text-accent-hi" aria-label={'Uploading, ' + u.percent + ' percent'}>{u.percent}%</span>
              )}
              <IconButton label={u.failed ? 'Remove' : 'Cancel upload'} className="relative h-6 w-6" onClick={() => cancelUpload(u.key)}>
                <svg width="12" height="12" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" fill="none">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </IconButton>
            </div>
          ))}
        </div>
      ) : null}

      {recording ? (
        <VoiceRecorder
          onClose={() => setRecording(false)}
          onAdd={(file) => void upload([file])}
        />
      ) : null}

      {pending.length ? (
        <div className="mb-2 flex flex-wrap gap-2">
          {/* What will go with the message: a thumbnail for a picture, a
              kind icon for everything else, each removable before Send. */}
          {pending.map((p) => (
            <span key={p.id} className="flex items-center gap-2 rounded-[var(--radius-s)] border border-line bg-surface-2 py-1.5 pr-1.5 pl-1.5 text-[12.5px] animate-[rise_200ms_var(--ease-out-soft)] motion-reduce:animate-none">
              {p.image ? (
                <img src={absolute(p.url)} alt="" width={40} height={40} className="h-10 w-10 flex-none rounded-[8px] object-cover" />
              ) : (
                <span className="grid h-10 w-10 flex-none place-items-center rounded-[8px] bg-surface-3 text-accent">
                  <Icon name={isVoiceNote(p) || p.type.startsWith('audio/') ? 'mic' : p.type.startsWith('video/') ? 'play' : 'paperclip'} size={17} />
                </span>
              )}
              <span className="min-w-0">
                <span className="block max-w-[160px] truncate font-semibold text-ink">{isVoiceNote(p) ? 'Voice message' : p.name}</span>
                <span className="block text-ghost">{humanSize(p.size)}</span>
              </span>
              {p.image || p.type.startsWith('video/') ? (
                <IconButton
                  label={spoilers.includes(p.id) ? 'Send ' + p.name + ' uncovered' : 'Send ' + p.name + ' as a spoiler'}
                  className={cn('h-6 w-6', spoilers.includes(p.id) && 'text-accent')}
                  onClick={() => setSpoilers((l) => l.includes(p.id) ? l.filter((x) => x !== p.id) : [...l, p.id])}
                >
                  <Icon name={spoilers.includes(p.id) ? 'eyeOff' : 'eye'} size={13} />
                </IconButton>
              ) : null}
              <IconButton label={'Remove ' + (isVoiceNote(p) ? 'voice message' : p.name)} className="h-6 w-6" onClick={() => setPending((l) => l.filter((x) => x.id !== p.id))}>
                <svg width="12" height="12" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" fill="none">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </IconButton>
            </span>
          ))}
        </div>
      ) : null}

      <div
        // Dropping a file on the composer, and pasting a screenshot into it,
        // are how people attach things - clicking a paperclip is the fallback.
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files?.length) void upload(Array.from(e.dataTransfer.files));
        }}
        className={cn(
          /* As the reference draws it: one white rounded field with the
             paperclip and emoji inside on the left and the send button on the
             right, lit when you are typing in it. */
          /* As the reference draws it: separate rounded tools, the message
             field between them, then Voice note and Send. */
          'flex items-end gap-2 rounded-[calc(18px*var(--radius-mul,1))] p-0.5',
          'transition-[background-color,box-shadow] duration-[250ms] ease-[var(--ease-out)]',
          dragging && 'bg-accent-soft shadow-[0_0_0_2px_var(--accent)]'
        )}
      >
        {/*
          * A real button that opens the picker, rather than a label wrapping
          * a display:none input. Electron will not always open the dialog for
          * an input that is not rendered, which is why attaching did nothing
          * in the desktop app while working in a browser.
          */}
        <button
          type="button"
          aria-label="Attach a file"
          title="Attach a file"
          /*
           * A sheet on a phone, the file dialog on a desktop.
           *
           * Tapping + and getting the iOS document browser full-screen is a
           * detour; the thing people want nine times out of ten is a photo,
           * and it should be one tap away in the same place the keyboard and
           * the emoji sheet already appear.
           */
          onClick={() => {
            if (document.body.dataset.narrow) { setAttachSheet(true); ref.current?.blur(); }
            else fileRef.current?.click();
          }}
          className={cn(TOOL, 'relative')}
        >
          <input
            ref={fileRef}
            type="file"
            multiple
            aria-label="Attach files"
            // Off-screen rather than display:none, so the dialog always opens.
            className="pointer-events-none absolute h-px w-px opacity-0"
            tabIndex={-1}
            onChange={(e) => {
            /*
             * Copy the files out before clearing the input.
             *
             * e.target.files is a live FileList, not a snapshot - setting
             * value to an empty string emptied the very list we were about
             * to upload, so picking a file did nothing at all. The clear is
             * still needed, or choosing the same file twice fires no change
             * event the second time.
             */
            const picked = Array.from(e.target.files ?? []);
            e.target.value = '';
            if (picked.length) void upload(picked);
          }}
          />
          {/* A paperclip, not a plus - it attaches, it does not create. */}
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m20.5 11.2-8.3 8.3a5.3 5.3 0 0 1-7.5-7.5l8.6-8.6a3.5 3.5 0 0 1 5 5l-8.6 8.6a1.8 1.8 0 0 1-2.5-2.5l7.9-7.9" />
          </svg>
        </button>

        {/* Photos and video on their own button, separate from any file. */}
        <button type="button" aria-label="Add a photo or video" title="Photo or video" className={cn(TOOL, 'hidden @md:grid')} onClick={() => openPicker('image/*,video/*')}>
          <Icon name="image" size={19} />
        </button>

        <span ref={emojiBtn} className="relative flex-none">
          <IconButton
            label={picker ? 'Keyboard' : 'Emoji'}
            className={TOOL}
            onClick={() => {
              /*
               * On a phone the two swap places.
               *
               * Opening the sheet takes the keyboard down, and pressing it
               * again puts the keyboard back - which is the only way to get
               * back to typing once the sheet has the bottom of the screen.
               */
              const next = !picker;
              setPicker(next);
              if (!document.body.dataset.narrow) return;
              if (next) ref.current?.blur();
              else requestAnimationFrame(() => ref.current?.focus());
            }}
          >
            {picker ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="2.6" y="6.4" width="18.8" height="11.2" rx="2.2" />
                <path d="M6.4 10h.01M9.6 10h.01M12.8 10h.01M16 10h.01M17.6 10h.01M6.4 13.2h.01M9.6 13.2h.01M12.8 13.2h.01M16 13.2h.01M8.4 16h7.2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="12" r="9" />
                <path d="M8.4 14.2a4.4 4.4 0 0 0 7.2 0" />
                <circle cx="9" cy="9.6" r="1" fill="currentColor" stroke="none" />
                <circle cx="15" cy="9.6" r="1" fill="currentColor" stroke="none" />
              </svg>
            )}
          </IconButton>
          {picker ? (
            <EmojiPicker
              anchor={emojiBtn.current}
              onClose={() => setPicker(false)}
              onPick={(text) => {
                // Inserted at the caret, not appended - people open the
                // picker mid-sentence more often than at the end.
                const box = ref.current;
                const at = box ? box.selectionStart : value.length;
                setValue(value.slice(0, at) + text + value.slice(box ? box.selectionEnd : value.length));
                requestAnimationFrame(() => {
                  // Only take focus back on a desktop. On a phone that would
                  // raise the keyboard over the sheet after every single
                  // emoji, so the sheet stays and you keep picking.
                  if (!document.body.dataset.narrow) box?.focus();
                  box?.setSelectionRange(at + text.length, at + text.length);
                });
              }}
            />
          ) : null}
        </span>

        <textarea
          ref={ref}
          rows={1}
          value={value}
          /* The placeholder disappears the moment you type, so it cannot be
             the only name the field has. */
          aria-label={placeholder ?? 'Write a message'}
          // Not disabled while a send is in flight. Disabling it makes the
          // browser drop focus, so every message cost you the caret - and
          // there is no reason you cannot start typing the next one.
          disabled={disabled}
          placeholder={placeholder ?? 'Write a message…'}
          spellCheck={settings?.spellcheck !== false}
          onChange={(e) => {
            setValue(e.target.value);
            if (settings?.typingIndicators !== false) onTyping?.();
            // Reads the word the caret is in, so the list follows the typing.
            setMention(people?.length ? mentionAt(e.target.value, e.target.selectionStart ?? 0) : null);
          }}
          onBlur={() => setMention(null)}
          onPaste={(e) => {
            const files = e.clipboardData?.files;
            if (files && files.length) { e.preventDefault(); void upload(Array.from(files)); }
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            const send = enterSends ? !e.shiftKey && !e.ctrlKey && !e.metaKey : e.ctrlKey || e.metaKey;
            if (send) { e.preventDefault(); void submit(); }
          }}
          className={cn(
            'max-h-[180px] min-h-[46px] min-w-0 flex-1 resize-none rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 px-4 py-[12px] text-[length:var(--chat-fs,16px)] leading-[1.4] outline-none placeholder:text-ghost',
            'shadow-[inset_0_0_0_1px_var(--line-soft)] transition-shadow duration-[250ms] ease-[var(--ease-out)] focus:shadow-[inset_0_0_0_1px_var(--accent),0_0_0_3px_var(--accent-soft)]'
          )}
        />

        {/* A question for the room, which lands as a message in it. */}
        {poll ? (
          <button
            type="button"
            aria-label="Ask the room a question"
            title="Poll"
            disabled={disabled}
            onClick={() => setAsking(true)}
            className={cn(TOOL, 'hidden @lg:grid')}
          >
            <Icon name="chart" size={19} />
          </button>
        ) : null}

        {/* A voice message: opens the recorder, which asks for the microphone. */}
        <button
          type="button"
          aria-label={recording ? 'Voice message recorder is open' : 'Record a voice message'}
          title="Voice note"
          disabled={disabled || recording}
          onClick={() => setRecording(true)}
          className={cn(TOOL, 'flex w-auto items-center gap-2 px-3 @lg:px-4', recording && 'text-bad')}
        >
          <Icon name="mic" size={19} />
          <span className="hidden text-[13.5px] font-semibold @lg:inline">Voice note</span>
        </button>

        {/* Say why the send is not happening. A button that has quietly
            stopped working is indistinguishable from a broken one. */}
        {coolLeft ? (
          <span className="flex-none self-center px-1 text-[11.5px] font-semibold text-warn tabular-nums">
            {coolLeft}s
          </span>
        ) : null}

        <Button
          kind="primary"
          size="sm"
          disabled={disabled || busy || (!value.trim() && !pending.length)}
          /* Icon only, so it needs a name of its own. */
          aria-label={busy ? 'Sending' : 'Send message'}
          onClick={() => void submit()}
          /* Rounded, not round. Nothing in this app is a circle except a
             face, a status dot and a count - a circular button among square
             ones reads as a different kind of control. */
          className="h-11 min-w-11 flex-none gap-2 rounded-[calc(14px*var(--radius-mul,1))]! px-3 shadow-[0_6px_16px_-6px_color-mix(in_srgb,var(--accent)_75%,transparent)] transition-transform duration-150 active:scale-95 @lg:px-5"
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 3 10.5 13.5M21 3l-6.8 18-3.7-7.5L3 9.8Z" />
          </svg>
          <span className="hidden text-[14px] font-bold @lg:inline">Send</span>
        </Button>
      </div>

      {asking && poll ? <NewPoll scope={poll.scope} channelId={poll.channelId} onClose={() => setAsking(false)} /> : null}

      {/* The phone attachment sheet, in the same place the keyboard and the
          emoji sheet come up. */}
      <Sheet open={attachSheet} onClose={() => setAttachSheet(false)}>
        <div className="grid grid-cols-2 gap-2 p-3">
          <AttachChoice
            icon="layers"
            label="Photos"
            note="Pictures and video"
            onClick={() => { setAttachSheet(false); openPicker('image/*,video/*'); }}
          />
          <AttachChoice
            icon="download"
            label="Files"
            note="Anything else"
            onClick={() => { setAttachSheet(false); openPicker(''); }}
          />
        </div>
      </Sheet>
    </div>
  );
}

/** One choice on the attachment sheet. */
function AttachChoice({ icon, label, note, onClick }: {
  icon: string;
  label: string;
  note: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-start gap-2 rounded-[var(--radius-l)] border border-line-soft bg-surface-2 p-3.5 text-left transition-colors active:bg-surface-3"
    >
      <span className="grid h-10 w-10 place-items-center rounded-[calc(12px*var(--radius-mul,1))] bg-accent-soft text-accent-hi">
        <Icon name={icon} size={19} />
      </span>
      <span className="block text-[13.5px] font-bold">{label}</span>
      <span className="block text-[11.5px] text-mute">{note}</span>
    </button>
  );
}

function absolute(url: string): string {
  return /^https?:/i.test(url) ? url : serverUrl() + url;
}

function humanSize(bytes: number): string {
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(1).replace(/\.0$/, '') + ' MB';
  if (bytes >= 1024) return Math.round(bytes / 1024) + ' KB';
  return bytes + ' B';
}

/**
 * Reporting one message.
 *
 * The report endpoint is about people rather than messages, so the message is
 * quoted into the note - a moderator opening the queue can see what was said
 * without having to go and find it.
 */
function ReportMessage({ msg, onClose }: { msg: Message; onClose: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function send() {
    if (!msg.user) return;
    setBusy(true);
    try {
      /* The id, not a pasted copy: the server takes its own snapshot of
         this message and the conversation round it, so deleting it
         afterwards does not take the evidence with it. */
      await api.post('/api/reports', {
        userId: msg.userId,
        messageId: msg.id,
        reason: 'inappropriate',
        note
      });
      toast('Report sent to the moderators', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Report this message"
      subtitle={msg.user ? 'From ' + msg.user.displayName : undefined}
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="danger" disabled={busy} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send report'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-[var(--gap)]">
        <div className="rounded-[var(--radius-s)] border border-line-soft bg-surface-1 p-2.5 text-[12.5px] text-dim">
          {msg.body || 'An attachment'}
        </div>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={400}
          placeholder="What is wrong with it? Optional."
        />
      </div>
    </Modal>
  );
}

/**
 * Scrolls a message into view and flashes it.
 *
 * Done by hand rather than with :target because the hash is the route - putting
 * a message id in it would navigate away from the conversation.
 */
function jumpTo(messageId: string) {
  const node = document.getElementById('msg-' + messageId);
  if (!node) return;
  node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  node.classList.add('msg-flash');
  setTimeout(() => node.classList.remove('msg-flash'), 1400);
}
